import { cloneDeep, isArray, isNull, isPlainObject } from 'lodash'
import formatDate from '@utils/format-date-with-time'
import api from '@utils/api'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { mapState } from 'vuex'
import userHasRoles from '@utils/userPermissions'

export default {
  props: {
    filters: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      data: [],
      rowsPerPage: [10, 25, 50],
      pagination: {},
      paginationEventCount: 0,
      paginationIsReady: false,
      allowedRolesToSeeDetails: [
        'SUPERUSER',
        'DICTIONARIES_OPERATOR',
        'DOCUMENTS_OPERATOR',
        'GUEST',
      ],
    }
  },
  computed: {
    ...mapState({
      unitsMeasurementValue: state => state.userSettings.userSettings['unitsMeasurement']
    }),
    filteredData() {
      if (!this.filters) return this.data
      let data = [...this.data]
      const filteredOrganizations = this.getFilteredOrganizations()
      const { oil, serial } = this.filters.common
      data = data.filter(elem =>
        filteredOrganizations.includes(elem.organisation)
      )
      if (oil && oil.length) data = data.filter(elem => oil.includes(elem.oil))
      if (serial) data = data.filter(elem => serial === elem.serial)
      const { custom } = this.filters
      if (!custom) return data
      Object.keys(custom).forEach(key => {
        const item = custom[key]
        if (isNull(item)) return
        if (isArray(item) && item.length) {
          data = data.filter(elem => item.includes(elem[key]))
        }
        if (isPlainObject(item)) {
          data = this.filterItems(data, item, key)
        }
      })
      return data
    },
    unitsMeasurement() {
      return this.$store.getters['userSettings/getUnitsMeasurement']
    },
    userAllowedToNavigate() {
      return userHasRoles(
        this.$store.state.auth.userRole,
        this.allowedRolesToSeeDetails
      )
    },
  },
  methods: {
    getPaginator() {
      const pagination = this.$store.getters['pagination/get'](this.modelName)
      if (pagination && pagination.page) {
        this.pagination = cloneDeep(pagination)
      } else {
        this.pagination = {
          page: 1,
          rowsPerPage: 10,
          sortBy: 'organisation',
          descending: false,
        }
      }
      this.paginationIsReady = true
    },
    onPaginationChange(pagination) {
      if (!this.paginationIsReady) return

      if (this.paginationEventCount !== -1)
        // ---- TODO remove after vuejs upgrade ----
        this.paginationEventCount += 1 // ---- TODO remove after vuejs upgrade ----
      if (this.paginationEventCount === -1) {
        // ---- TODO remove after vuejs upgrade ----

        this.pagination.page = pagination.page
        this.pagination.sortBy = pagination.sortBy
        this.pagination.descending = pagination.descending
        this.pagination.rowsPerPage = pagination.rowsPerPage
        this.persistPagination()
        this.$store.dispatch(
          'pagination/saveRowsPerPage',
          this.pagination.rowsPerPage
        )
      }
      if (this.paginationEventCount === 1)
        // ---- TODO remove after vuejs upgrade ----
        this.paginationEventCount = -1 // ---- TODO remove after vuejs upgrade ----
    },
    resetPage() {
      if (this.paginationIsReady) {
        this.pagination.page = 1
        this.persistPagination()
      }
    },
    persistPagination() {
      this.$store.commit('pagination/SET', {
        name: this.modelName,
        pagination: cloneDeep(this.pagination),
      })
    },
    getValue(dictionaryName, value) {
      return this.$store.getters['data/getValueFromDictionary'](
        dictionaryName,
        Number(value)
      )
    },
    clearFilters() {},
    dateFormat(date) {
      const formatedDate = formatDate(date)
      return formatedDate === 'Invalid Date' ? '' : formatedDate
    },
    getRemains() {
      return api
        .getItems('remain', {
          size: 999999,
          page: 1,
          filter_shipment_value: 'true',
          filter_shipment_comparison: 'EQ',
        })
        .then(({ content }) => content)
    },
    async getOil(id) {
      const { drumOil } = await api.getGoodsEquippedbarrel(id)
      const { oil } = await api.getGoodsDrumoil(drumOil)
      return oil
    },
    isNumeric(value) {
      return !isNaN(value)
    },
    isNull(value) {
      return value === null || value === undefined || value === ''
    },
    isInRange(value, rangeFrom, rangeTo) {
      if (rangeTo !== '' && rangeFrom !== '') {
        if (
          this.isNumeric(rangeTo) &&
          this.isNumeric(rangeFrom) &&
          this.isNumeric(value)
        ) {
          let intValue = parseInt(value)
          return (
            intValue >= parseInt(rangeFrom) && intValue <= parseInt(rangeTo)
          )
        } else {
          return value >= rangeFrom && value <= rangeTo
        }
      } else if (rangeTo !== '') {
        if (this.isNumeric(rangeTo) && this.isNumeric(value)) {
          return parseInt(value) <= parseInt(rangeTo)
        } else {
          return value <= rangeTo
        }
      } else {
        if (this.isNumeric(rangeFrom) && this.isNumeric(value)) {
          return parseInt(value) >= parseInt(rangeFrom)
        } else {
          return value >= rangeFrom
        }
      }
    },
    filterItems(data, item, key) {
      const { comparison, value, organizationProperty } = item
      if (isNull(value)) return data
      switch (comparison) {
        case 'equal':
          return data.filter(
            elem =>
              elem[key] ===
              (organizationProperty
                ? value
                : value * this.unitsMeasurement.volumeMultiplier)
          )
        case 'more':
          return data.filter(
            elem =>
              elem[key] >
              (organizationProperty
                ? value
                : value * this.unitsMeasurement.volumeMultiplier)
          )
        case 'less': {
          return data.filter(
            elem =>
              elem[key] <
              (organizationProperty
                ? value
                : value * this.unitsMeasurement.volumeMultiplier)
          )
        }
        case 'notEqual':
          return data.filter(
            elem =>
              elem[key] !==
              (organizationProperty
                ? value
                : value * this.unitsMeasurement.volumeMultiplier)
          )
        default:
          return data
      }
    },
    getFilteredOrganizations() {
      let organizations = this.$store.getters['data/getDictionary'](
        'organization'
      )
      const {
        region,
        city,
        organisation,
        postcodeFrom,
        postcodeTo,
      } = this.filters.common
      const { securityStock } = this.filters.custom
      if (region && region.length) {
        organizations = organizations.filter(elem =>
          region.includes(elem.region)
        )
      }
      if (city && city.length) {
        organizations = organizations.filter(elem => city.includes(elem.city))
      }
      if (organisation && organisation.length) {
        organizations = organizations.filter(elem =>
          organisation.includes(elem.id)
        )
      }
      if (postcodeFrom !== '' || postcodeTo !== '') {
        organizations = organizations.filter(elem =>
          this.isInRange(elem.postcode, postcodeFrom, postcodeTo)
        )
      }
      if (securityStock && securityStock.value) {
        organizations = this.filterItems(
          organizations.map(org => ({
            ...org,
            securityStock: org.securityStock || 0,
          })),
          securityStock,
          'securityStock'
        )
      }
      return organizations.map(elem => elem.id)
    },
    export() {
      if (!this.data || !this.data.length) return
      let csv = ''
      // Generate first row
      const firstRow = this.columns.map(elem => this.$t(`label__${elem.text}`))
      csv += `${firstRow.join(';')}\n`
      const columns = this.columns.map(elem => elem.value)
      // Generate data rows
      this.data.forEach(row => {
        const tmp = []
        columns.forEach(column => {
          let value = null
          switch (column) {
            case 'organisation':
            case 'plugOrg':
            case 'transmitterOrg':
              value = this.getValue('organization', row[column])
              break
            case 'oil':
              value = this.getValue('goods', row[column])
              break
            case 'date':
            case 'measureTime':
              value = this.dateFormat(row[column])
              break
            case 'avgAmount':
            case 'lastAmount':
            case 'amount':
            case 'initialAmount':
            case 'currentAmount':
            case 'currentVolume':
            case 'averageDailyOilUsageDuringLastMonth':
            case 'averageDailyOilUsageDuringLastWeek':
            case 'sufficientForDays':
            case 'prevAmount':
            case 'openedAmount':
            case 'difference':
              value = (
                row[column] / this.unitsMeasurement.volumeMultiplier
              ).toFixed(1)
              break
            default:
              value = row[column]
              break
          }
          tmp.push(value)
        })
        csv += `${tmp.join(';')}\n`
      })
      this.download(csv)
    },
    download(csv) {
      const name = this.$t(`reports__${this.$route.params.name}`)
      const date = moment().format('YYYY-MM-DD_HH-mm-ss')
      const file = new File([csv], `${name}_${date}.csv`, {
        type: 'text/plain;charset=utf-8',
      })
      saveAs(file)
    },
    convertTemperature(value) {
      if (this.unitsMeasurementValue !== 'us') {
        return `${value} ℃`
      } else {
        const fahrenheit = value * (9 / 5) + 32
        return `${fahrenheit.toFixed(1)} ℉`
      }
    },
  },
}
